import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import InputNumber from 'rc-input-number';
import BootstrapTable from 'react-bootstrap-table-next';
import ModalWindow from '../../common/Modal';
import Button from '../../common/Button';
import Switch from '../../common/Switch';
import Quantity from '../../blocks/Quantity';
import Availability from '../../blocks/Availability';
import Spinner from '../../common/Spinner';
import styles from './BuyProductModal.module.scss';
import history from '../../../utils/history';
import { currencyFormat, numberFormatter } from '../../../utils/formatHelper';
import { getDynamicColumn, getMax } from '../../../utils/tableHelper';
import { variantSort2, sortBySize } from '../../../utils/sortHelper';
import { sortCaret } from '../../../utils/tableHelper';
import { upperFirst } from 'lodash';
import { NO_VALUE_PLACEHOLDER } from '../../../constants/tables';

class BuyProductModal extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      cart: null,
      quantities: props.cartsData ? { ...props.cartsData.variants } : {},
      show_unavailable: false,
      horizontal_view: props.product ? props.product.variants.length > 10 : false,
      changed: false,
    };
  }

  dataFormatColumn(cell, row, rowIndex, field) {
    return row.options[field] ? row.options[field] : NO_VALUE_PLACEHOLDER;
  }

  updateCart = () => {
    this.props.updateCart(this.state.quantities);
    this.props.onClose();
  };

  checkOutNow = () => {
    const { retailerId } = this.props;

    this.updateCart();
    history.push(`/retailers/${retailerId}/reorder/cart`);
  };

  dataPrice(cell, row) {
    if (!cell) {
      return 'No available price';
    }

    return currencyFormat(cell, row.currency);
  }

  dataAvailability = (cell, row, rowIndex, { product, quantities, handleOnChangeInputSlider }) => {
    const max = getMax(product.variants);

    return (
      <Availability
        quantity={quantities[row.id] || 0}
        handle={handleOnChangeInputSlider}
        inventory={cell}
        maxOfAllInventory={max}
        variantId={row.id}
      />
    );
  };

  dataInput = (cell, row, rowInedx, { quantities, handleOnChangeInputSlider }) => {
    return row.available_inventory > 0 && row.wholesale_price > 0 ? (
      <Quantity
        value={quantities[row.id] || 0}
        onQuantityChange={handleOnChangeInputSlider}
        variantId={row.id}
        max={row.available_inventory}
      />
    ) : (
      NO_VALUE_PLACEHOLDER
    );
  };

  handleOnChangeInputSlider = (quantity, variantId) => {
    this.setState((prevState) => {
      return {
        quantities: {
          ...prevState.quantities,
          [variantId]: +quantity,
        },
        changed: true,
      };
    });
  };

  hasUnavailable() {
    const { product } = this.props;
    if (!product || !product.variants) {
      return false;
    }

    return product.variants.some((variant) => variant.available_inventory === 0);
  }

  hasHorizontalView() {
    const { product } = this.props;
    if (!product || !product.variants) {
      return false;
    }

    return Object.keys(product.variants[0].options).length >= 2;
  }

  renderVerticalView() {
    const { product } = this.props;
    const { show_unavailable } = this.state;

    const dynamicColumns = getDynamicColumn(product.variants);
    const variants = show_unavailable ? product.variants : product.variants.filter((v) => v.available_inventory);
    const columns = [];

    dynamicColumns.forEach((option) => {
      columns.push({
        text: upperFirst(option),
        dataField: option,
        formatter: this.dataFormatColumn,
        formatExtraData: option,
        sort: true,
      });
    });

    columns.push(
      {
        text: 'Price',
        dataField: 'wholesale_price',
        formatter: this.dataPrice,
        sort: true,
      },
      {
        text: 'Quantity',
        dataField: 'quantity',
        formatter: this.dataInput,
        formatExtraData: {
          quantities: this.state.quantities,
          handleOnChangeInputSlider: this.handleOnChangeInputSlider,
        },
      },
      {
        text: 'Availability',
        dataField: 'available_inventory',
        formatter: this.dataAvailability,
        formatExtraData: {
          product: this.props.product,
          quantities: this.state.quantities,
          handleOnChangeInputSlider: this.handleOnChangeInputSlider,
        },
        sort: true,
      },
    );

    return (
      <BootstrapTable
        data={variants}
        columns={columns}
        keyField="id"
        striped
        hover
        bordered={false}
        className={styles.container}
        sort={{
          sortCaret,
          sortFunc: variantSort2,
        }}
        defaultSorted={[
          {
            dataField: dynamicColumns.length ? dynamicColumns[0] : 'available_inventory',
            order: 'desc',
          },
        ]}
      ></BootstrapTable>
    );
  }

  newHorizontalRow = (dimension_column, variant) => {
    return {
      column: dimension_column,
      currency: variant.currency,
      min_price: variant.wholesale_price,
      max_price: variant.wholesale_price,
      [dimension_column]: variant.options[dimension_column],
      ids: {},
      sizes: {},
    };
  };

  horizontalRows = () => {
    const { product } = this.props;
    const { show_unavailable } = this.state;

    const dynamicColumns = getDynamicColumn(product.variants);
    const dimensionColumn = dynamicColumns.find((option) => option !== 'size');

    const variants = show_unavailable ? product.variants : product.variants.filter((v) => v.available_inventory);

    return variants.reduce((rows, variant) => {
      const dimensionValue = variant.options[dimensionColumn];
      const size = variant.options.size;
      const row = rows[dimensionValue] ? rows[dimensionValue] : this.newHorizontalRow(dimensionColumn, variant);

      row.ids[size] = variant.id;
      row.sizes[size] = variant.available_inventory;
      row.min_price = Math.min(row.min_price, variant.wholesale_price);
      row.max_price = Math.max(row.max_price, variant.wholesale_price);
      rows[dimensionValue] = row;

      return rows;
    }, {});
  };

  renderHorizontalView() {
    const { quantities } = this.state;
    const rows = this.horizontalRows();

    return (
      <div className={styles.horizontal_view}>
        <ul className={styles.variant_wrapper}>
          {Object.keys(rows).map((key) => {
            const row = rows[key];
            const has_price_range = row.min_price !== row.max_price;

            const size_entries = Object.entries(row.sizes);
            size_entries.sort((a, b) => sortBySize(a[0], b[0]));

            return (
              <li key={key}>
                <div className={styles.info}>
                  <div>
                    <label>{upperFirst(row.column)}</label>: {row[row.column]}
                  </div>

                  {has_price_range ? (
                    <div>
                      <label>Price range:</label> {currencyFormat(row.min_price, row.currency)} -{' '}
                      {currencyFormat(row.max_price, row.currency)}
                    </div>
                  ) : (
                    <div>
                      {row.min_price > 0 ? (
                        <>
                          <label>Price:</label>
                          {currencyFormat(row.min_price, row.currency)}{' '}
                        </>
                      ) : (
                        <label>No available price</label>
                      )}
                    </div>
                  )}
                </div>

                <ul className={styles.variant_sizes}>
                  {size_entries.map(([size, available_inventory]) => {
                    const id = row.ids[size];
                    const quantity = quantities[row.ids[size]];
                    const hasPrices = row.min_price > 0 && row.max_price > 0;
                    return (
                      <li key={id}>
                        <div className={styles.size_header}>{size}</div>
                        <div className={styles.size_quantity}>
                          <InputNumber
                            min={0}
                            disabled={!hasPrices || !available_inventory}
                            onFocus={(e) => e.target.select()}
                            onClick={(e) => e.target.select()}
                            max={available_inventory}
                            value={quantity || 0}
                            onChange={(value) => this.handleOnChangeInputSlider(value, id)}
                            formatter={numberFormatter}
                          />
                          / {available_inventory}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  render() {
    const { onClose, product, open } = this.props;
    const { horizontal_view, show_unavailable, changed } = this.state;

    return (
      <ModalWindow open={open} onClose={onClose} size="xlarge">
        {product ? (
          <>
            <p className={styles.name}>{product.title}</p>

            <div className={styles.product_wrapper}>
              <aside>
                <div className={styles.model}>
                  <p>Model: {product.model}</p>
                </div>
                <div className={styles.square_images}>
                  <img src={product.variants[0].square_images[0]} alt="" />
                </div>
                <div className={styles.desc}>
                  <p>Description:</p>
                  {product.description.length > 250
                    ? product.description.substring(0, 290) + '...'
                    : product.description}
                </div>

                <div className={styles.view_actions}>
                  {this.hasUnavailable() ? (
                    <Switch
                      label="Show out of stock"
                      checked={show_unavailable}
                      onSwitch={(value) => this.setState({ show_unavailable: value })}
                    />
                  ) : null}

                  {this.hasHorizontalView() ? (
                    <Switch
                      label="Horizontal view"
                      checked={horizontal_view}
                      onSwitch={(value) => this.setState({ horizontal_view: value })}
                    />
                  ) : null}
                </div>
              </aside>
              {product.variants.length > 0 ? (
                horizontal_view ? (
                  this.renderHorizontalView()
                ) : (
                  this.renderVerticalView()
                )
              ) : (
                <div className={styles.spinner}>
                  <Spinner />
                </div>
              )}
            </div>

            <hr style={{ backgroundColor: '#eeeeee' }} />

            <div className={styles.footer}>
              <Button text="UPDATE CART" onClick={this.updateCart} disabled={!changed} />

              <Button type="submit" text="UPDATE AND CHECK OUT" onClick={this.checkOutNow} disabled={!changed} />
            </div>
          </>
        ) : (
          <div className={styles.spinnerFiller}>
            Loading Product Data...
            <div className={styles.preloader} />
          </div>
        )}
      </ModalWindow>
    );
  }
}

export default BuyProductModal;

BuyProductModal.defaultProps = {
  open: false,
};

BuyProductModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateCart: PropTypes.func.isRequired,
  cartsData: PropTypes.object,
  retailerId: PropTypes.number.isRequired,
};
